import { useBroadcastChannel } from '@vueuse/core';
import {
  AUTH_BROADCAST_CHANNEL,
  EAuthMessages,
  type IAuthChannelEventListener,
  type IAuthMessages
} from '@/types/composables/useAuthChannel.const';
import { useQueryClient } from '@tanstack/vue-query';
import { ref, watch, type Ref } from 'vue';
import type { IAccountResponse } from '@/types/services/auth.service';

export const useAuthChannel = () => {
  const queryClient = useQueryClient();
  const { channel, post } = useBroadcastChannel<IAuthMessages, IAuthMessages>({
    name: AUTH_BROADCAST_CHANNEL
  });

  const eventListeners: Ref<IAuthChannelEventListener[]> = ref([]);

  const addAuthChannelEventListener = (listener: IAuthChannelEventListener) => {
    eventListeners.value.push(listener);
  };

  const initializeAuthMessageWatcher = () => {
    let abortController: AbortController = new AbortController();

    watch(
      [channel, eventListeners],
      ([channel, eventListeners]) => {
        if (!channel) {
          return;
        }
        abortController.abort();
        console.debug('Added auth channel message listener');
        abortController = new AbortController();
        channel.addEventListener(
          'message',
          async (event: MessageEvent<IAuthMessages>) => {
            console.debug('Received message from auth broadcast channel', event.data);
            const data: IAuthMessages = event.data;
            switch (data.message) {
              case EAuthMessages.USER_LOGGED_IN:
                await queryClient.invalidateQueries({
                  queryKey: ['getAccounts']
                });
                break;
              case EAuthMessages.USER_LOGGED_OUT:
                queryClient.setQueryData<IAccountResponse[]>(
                  ['getAccounts'],
                  (oldData): IAccountResponse[] | undefined => {
                    if (!oldData) {
                      return undefined;
                    }

                    return oldData.filter((account) => account.uid.toString() !== data.uid);
                  }
                );
                break;
              case EAuthMessages.USER_REFRESHED:
                await queryClient.invalidateQueries({
                  queryKey: ['getAccounts']
                });
                break;
              default:
                return;
            }
          },
          { signal: abortController.signal }
        );
        for (const listener of eventListeners) {
          channel.addEventListener('message', listener, { signal: abortController.signal });
        }
      },
      { immediate: true }
    );
  };

  const postAfterLoggedIn = () => {
    console.debug('Posting after logged in message');
    post({
      message: EAuthMessages.USER_LOGGED_IN
    });
  };

  const postAfterLoggedOut = (uid: string) => {
    console.debug('Posting after logged out message');
    post({
      message: EAuthMessages.USER_LOGGED_OUT,
      uid
    });
  };

  const postPreRefresh = (uids: number[]) => {
    console.debug('Posting before refresh message');
    post({
      message: EAuthMessages.USER_REFRESHING,
      uids
    });
  };
  const postAfterRefresh = (uids: number[]) => {
    console.debug('Posting after refresh message');
    post({
      message: EAuthMessages.USER_REFRESHED,
      uids
    });
  };

  return {
    initializeAuthMessageWatcher,
    postAfterLoggedIn,
    postAfterLoggedOut,
    postPreRefresh,
    postAfterRefresh,
    addAuthChannelEventListener
  };
};
